export type ErrorDetails = {
  prompt?:string;
  category?: string;
  title?: string;
  message: string;
  action?: "GetHelp" | "TryAgain" | "UpgradePlan";
  displayPlace?: "card" | "prompt";
};

export const errors: Record<string, ErrorDetails> = {
  SAFETY: {
    category: "Safety Violations",
    action: "GetHelp",
    displayPlace: "card",
    title: "Unable to generate this image",
    message:
      "Your request contains content that may violate our safety guidelines. Please revise your prompt to avoid potentially harmful or inappropriate content.",
  },
  PROHIBITED_CONTENT: {
    category: "Safety Violations",
    action: "GetHelp",
    displayPlace: "card",
    title: "Unable to generate this image",
    message:
      "Your request contains content that may violate our safety guidelines. Please revise your prompt to avoid potentially harmful or inappropriate content.",
  },
  BLOCKLIST: {
    category: "Safety Violations",
    action: "GetHelp",
    displayPlace: "card",
    title: "Unable to generate this image",
    message:
      "Your request contains content that may violate our safety guidelines. Please revise your prompt to avoid potentially harmful or inappropriate content.",
  },
  COPYRIGHT: {
    category: "Copyright Concerns",
    action: "GetHelp",
    displayPlace: "card",
    title: "Potential copyright concern detected",
    message:
      "Your request appears to include protected characters, branded content, or other copyrighted material. Please revise to use original concepts.",
  },
  RECITATION: {
    category: "Copyright Concerns",
    action: "GetHelp",
    displayPlace: "card",
    title: "Potential copyright concern detected",
    message:
      "Your request appears to include protected characters, branded content, or other copyrighted material. Please revise to use original concepts.",
  },
  SERVICE_UNAVAILABLE: {
    category: "Technical Errors",
    action: "TryAgain",
    displayPlace: "card",
    title: "Image generation service unavailable",
    message:
      "We're currently experiencing technical difficulties with our image generation service. Our team has been notified.",
  },
  INTERNAL_ERROR: {
    category: "Technical Errors",
    action: "TryAgain",
    displayPlace: "card",
    title: "Image generation service unavailable",
    message:
      "We're currently experiencing technical difficulties with our image generation service. Our team has been notified.",
  },
  OTHER: {
    category: "Technical Errors",
    action: "TryAgain",
    displayPlace: "card",
    title: "Image generation service unavailable",
    message:
      "We're currently experiencing technical difficulties with our image generation service. Our team has been notified.",
  },
  FINISH_REASON_UNSPECIFIED: {
    category: "Technical Errors",
    action: "TryAgain",
    displayPlace: "card",
    title: "Image generation service unavailable",
    message:
      "We're currently experiencing technical difficulties with our image generation service. Our team has been notified.",
  },
  TIMEOUT: {
    category: "Timeout",
    action: "TryAgain",
    displayPlace: "card",
    title: "Image generation timed out",
    message:
      "The request took too long to process. This may be due to high system load or complexity of the request.",
  },
  SPII: {
    category: "Security & Compliance",
    action: "TryAgain",
    displayPlace: "card",
    title: "Sensitive information detected",
    message:
      "Your prompt appears to contain sensitive personally identifiable information. For security reasons, we cannot process this request. Please revise your prompt to remove any sensitive information.",
  },
  ALL: {
    category: "Input-Related Errors",
    action: "GetHelp",
    displayPlace: "card",
    title: "Unable to process this prompt",
    message:
      "Your prompt doesn't contain enough information for image generation. Please provide more details about what you'd like to create.",
  },
  MAX_TOKENS: {
    category: "Token Limit",
    action: "GetHelp",
    displayPlace: "card",
    title: "Generation Limit Reached",
    message:
      "Your request exceeds the maximum token limit. This typically happens with very complex or detailed prompts.",
  },
  CREDITS_LIMIT: {
    category: "Quota Exceeded",
    action: "UpgradePlan",
    displayPlace: "card",
    title: "Image generation quota exceeded",
    message:
      "You've reached your plan's limit for image generations for [period:today | this month]. Quota will reset on [date: tomorrow | iso_date]. Consider upgrading your plan to continue generating more images.",
  },
  ERR_BAD_REQUEST: {
    category: "Input-Related Errors",
    action: "GetHelp",
    displayPlace: "card",
    title: "Unable to process this prompt",
    message:
      "Your prompt contains information that can not be processed for image generation. Please provide more details or remove any sensitive information from the prompt.",
  },
   INVALID_ARGUMENT: {
    category: "Input-Related Errors",
    action: "GetHelp",
    displayPlace: "card",
    title: "Unable to process this prompt",
    message:
      "Your prompt contains information that can not be processed for image generation. Please provide more details or remove any sensitive information from the prompt.",
  },
  MALFORMED_FUNCTION_CALL: {
    category: "Input-Related Errors",
    action: "GetHelp",
    displayPlace: "card",
    title: "Unable to process this prompt",
    message:
      "Your prompt contains information that can not be processed for image generation. Please provide more details or remove any sensitive information from the prompt.",
  },
  PROMPT_LENGTH_EXCEEDED: {
    category: "Prompt Length Exceeded",
    action: "TryAgain",
    displayPlace: "prompt",
    title: "Prompt exceeds maximum length",
    message:
      "Your prompt is too long (max 480 tokens). Please shorten your prompt to continue.",
  },
  UNSUPPORTED_LANGUAGE: {
    category: "Language Not Supported",
    action: "TryAgain",
    displayPlace: "prompt",
    title: "Unsupported language detected",
    message:
      "We detected {lang} in your prompt. Currently supported languages are: English, Spanish, Japanese, Chinese, Hindi.",
  },
  MAX_FILES_EXCEEDED: {
    category: "Too Many Files",
    action: "TryAgain",
    displayPlace: "prompt",
    title: "Maximum file limit exceeded",
    message:
      "You've attached {file_count} files. The maximum allowed is 2. Please remove one or more files to continue.",
  },
  UNSUPPORTED_FILE_FORMAT: {
    category: "Unsupported File Format",
    action: "TryAgain",
    displayPlace: "prompt",
    title: "File format not supported",
    message:
      "The file {filename} cannot be processed. Only images in PNG, JPEG, WEBP, HEIC, or HEIF format are supported.",
  },
};


interface ErrorCodeEntry {
  category: string;
  description: string;
  filtered: string;
}

export const errorCodes:{ [key: string]: ErrorCodeEntry } = {
  "58061214": {
    category: "Child",
    description: "Detects child content where it isn't allowed due to the API request settings or allowlisting.",
    filtered: "input (prompt): 58061214, output (image): 17301594"
  },
  "17301594": {
    category: "Child",
    description: "Detects child content where it isn't allowed due to the API request settings or allowlisting.",
    filtered: "input (prompt): 58061214, output (image): 17301594"
  },
  "29310472": {
    category: "Celebrity",
    description: "Detects a photorealistic representation of a celebrity in the request.",
    filtered: "input (prompt): 29310472, output (image): 15236754"
  },
  "15236754": {
    category: "Celebrity",
    description: "Detects a photorealistic representation of a celebrity in the request.",
    filtered: "input (prompt): 29310472, output (image): 15236754"
  },
  "62263041": {
    category: "Dangerous content",
    description: "Detects content that's potentially dangerous in nature.",
    filtered: "input (prompt)"
  },
  "57734940": {
    category: "Hate",
    description: "Detects hate-related topics or content.",
    filtered: "input (prompt): 57734940, output (image): 22137204"
  },
  "22137204": {
    category: "Hate",
    description: "Detects hate-related topics or content.",
    filtered: "input (prompt): 57734940, output (image): 22137204"
  },
  "74803281": {
    category: "Other",
    description: "Detects other miscellaneous safety issues with the request",
    filtered: "input (prompt): 42876398, output (image): 29578790, 74803281"
  },
  "29578790": {
    category: "Other",
    description: "Detects other miscellaneous safety issues with the request",
    filtered: "input (prompt): 42876398, output (image): 29578790, 74803281"
  },
  "42876398": {
    category: "Other",
    description: "Detects other miscellaneous safety issues with the request",
    filtered: "input (prompt): 42876398, output (image): 29578790, 74803281"
  },
  "39322892": {
    category: "People/Face",
    description: "Detects a person or face when it isn't allowed due to the request safety settings.",
    filtered: "output (image)"
  },
  "92201652": {
    category: "Personal information",
    description: "Detects Personally Identifiable Information (PII) in the text, such as the mentioning a credit card number, home addresses, or other such information.",
    filtered: "input (prompt)"
  },
  "89371032": {
    category: "Prohibited content",
    description: "Detects the request of prohibited content in the request.",
    filtered: "input (prompt): 89371032, output (image): 49114662, 72817394"
  },
  "49114662": {
    category: "Prohibited content",
    description: "Detects the request of prohibited content in the request.",
    filtered: "input (prompt): 89371032, output (image): 49114662, 72817394"
  },
  "72817394": {
    category: "Prohibited content",
    description: "Detects the request of prohibited content in the request.",
    filtered: "input (prompt): 89371032, output (image): 49114662, 72817394"
  },
  "90789179": {
    category: "Sexual",
    description: "Detects content that's sexual in nature.",
    filtered: "input (prompt): 90789179, output (image): 63429089, 43188360"
  },
  "63429089": {
    category: "Sexual",
    description: "Detects content that's sexual in nature.",
    filtered: "input (prompt): 90789179, output (image): 63429089, 43188360"
  },
  "43188360": {
    category: "Sexual",
    description: "Detects content that's sexual in nature.",
    filtered: "input (prompt): 90789179, output (image): 63429089, 43188360"
  }
};